import type { WrittenPage } from '../../sections'
import { LINKS } from '../links'

/**
 * Generative AI liderlik rehberi e-kitabı — Türkçe.
 *
 * Bölüm sırası, görseller ve maddeler İngilizcesiyle birebir aynı; değişen
 * yalnızca nasıl söylendiği. E-kitabın adı kapağında basılı olduğu için
 * İngilizce kalıyor; "Generative AI", "governance", "chatbot" gibi yerleşik
 * terimler ve marka adları da öyle.
 *
 * İndirilen dosya İngilizce — e-kitabın Türkçesi yok. Bu, sayfayı Türkçe
 * yazmamak için bir sebep değil: sayfayı okuyup indirme kararını veren kişi
 * Türkiye'deki okur, ve karar Türkçe veriliyor.
 *
 * Formla ilgili açık soru İngilizce sayfada duruyor ve orada bir kez duruyor:
 * `check` iki dilde tekrarlanınca aynı karar iki not olarak sayılıyor.
 */
export const ebookGenerativeAi: WrittenPage = {
  navLabel: 'Generative AI rehberi',
  title: 'The Leader’s Guide to Generative AI',
  description:
    'Generative AI’ın gücünü işinize taşımaya hazır mısınız? Airbus, Sephora, DHL ve Spotify örnekleriyle uygulanabilir içgörüler sunuyoruz. Digitopia Impact Labs’in e-kitabını indirin.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'İşte mümkün olanı yeniden tanımlamak',
      headline: 'The Leader’s Guide to Generative AI',
      body: [
        'Generative AI’ın gücünü işinize taşımaya hazır mısınız? Kapsamlı e-kitabımız, yeniliği '
          + 'sürdürmeniz ve rekabette önde kalmanız için uygulanabilir içgörüler ve sahadan '
          + 'gerçek örnekler sunuyor.',
      ],
      ctas: [{ label: 'İndirin', href: LINKS.generativeAiEbookPdf }],
      photo: {
        src: '/photo/generative-ai-ebook-artwork.webp',
        alt:
          'E-kitabın kapak görseli: uzun pozlamayla çekilmiş, uzaktaki bir noktaya doğru akan '
          + 'macenta ve yeşil ışık çizgilerinden oluşan bir tünel.',
      },
      photoFrame: 'square',
    },
    {
      kind: 'cta',
      tone: 'card',
      flip: true,
      heading: 'Bu e-kitabı neden indirmelisiniz?',
      points: [
        'Uygulanabilir stratejiler: Generative AI’ı ürün geliştirmeden müşteri hizmetlerine '
          + 'kadar farklı iş fonksiyonlarında nasıl hayata geçireceğinizi öğrenin.',
        'Sahadan gerçek örnekler: Airbus, Sephora, DHL ve Spotify gibi sektör liderlerinin '
          + 'büyümek için AI’ı nasıl kullandığını görün.',
        'Somut içgörüler: AI teknolojilerini işinize entegre etmek ve bir yenilik kültürü '
          + 'kurmak için adım adım yol gösterim.',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: LINKS.generativeAiEbookPdf }],
      photo: {
        src: '/photo/generative-ai-ebook-covers.webp',
        alt:
          'The Leader’s Guide to Generative AI’ın iki kopyası, biri diğerinin arkasında, beyaz '
          + 'zeminde.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Neler öğreneceksiniz?',
      points: [
        'Ürün geliştirmede yenilik: AI ile ürün tasarımını hızlandırın ve yeni varyasyonlar '
          + 'üretin. Örnek: Autodesk’in Generative Design aracı Airbus’ın ağırlığı %45 '
          + 'azaltmasına nasıl yardım etti?',
        'Müşteri deneyimini güçlendirmek: kişiselleştirilmiş içerik sunun, müşteri etkileşimini '
          + 'yükseltin. Örnek: Sephora’nın kişiye özel güzellik önerileri veren AI chatbot’u.',
        'Operasyonu sadeleştirmek: süreçleri otomatikleştirin, kaynakları optimize edin. Örnek: '
          + 'DHL’in AI destekli lojistik optimizasyonu.',
        'AI içgörüleriyle yeniliği sürüklemek: stratejik içgörü üretmek için veriyi kullanın. '
          + 'Örnek: Spotify’ın müzik önerilerini güçlendiren AI tahminleri.',
        'Etik AI kullanımını güvence altına almak: sorumlu AI için sağlam governance '
          + 'çerçeveleri kurun. Örnek: Microsoft’un AI adaleti için geliştirdiği Fairlearn '
          + 'aracı.',
      ],
      ctas: [{ label: 'E-kitabı indirin', href: LINKS.generativeAiEbookPdf }],
      photo: {
        src: '/photo/generative-ai-ebook-stack.webp',
        alt:
          'The Leader’s Guide to Generative AI’ın basılı kopyalarından oluşan bir yığın, '
          + 'yukarıdan görünüm.',
      },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Kaçırmayın!',
      sub:
        'Ücretsiz e-kitabımızı bugün indirin ve işinizi Generative AI ile dönüştürmeye '
        + 'başlayın.',
      cta: { label: 'E-kitabı indirin', href: LINKS.generativeAiEbookPdf },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'Müşterilerimiz ne diyor?',
      items: [
        {
          quote:
            'Digitopia’nın DMI değerlendirmeleri, net ve uygulanabilir dijital yol haritaları '
            + 'kurmamıza ve o yolda kalmamıza yardımcı oldu. İçgörüleri paha biçilmezdi; AI '
            + 'daha büyük bir gündem maddesi hâline gelmeden çok önce bizi bu kritik alanlara '
            + 'odaklanmaya yönlendirdiler. Digitopia bizim için yalnızca bir tedarikçi değil; '
            + 'dijital stratejimizi şekillendiren ve ilerleten temel bir rol üstleniyor.',
          source: 'Fatih Akar, Director of Global Information Technologies',
          company: 'Kordsa',
          logo: { name: 'Kordsa', src: '/logo/kordsacard.png' },
          portrait: { src: '/photo/portrait-fatih-akar.png', alt: 'Fatih Akar' },
        },
      ],
    },
  ],
}
